import React, { useEffect } from 'react';
import { CardTheme, TextLarge, RenderForm, ButtonTheme } from '../../../components';
import { StyledContainer } from './styles';
import { useForm } from 'react-hook-form';
import { Row, Col, Spin } from 'antd';
import { FORM_CLAIM_DATA, FORM_CLAIM_CREATE } from './forms';
import { useCallback, useState } from 'preact/hooks';
import { ContainerButton } from '../../../styles/global-style';
import { onUploadFile } from './hooks/onUploadFile';
import { handleSubmitForm } from './func/submitHandler';
import { useOptions } from './hooks/useOptions';
import { useTeleQuery } from './hooks/useTeleQuery';
import { useZipcode } from './hooks/useZipcodeQuery';
import {
  channelClaimQuery,
  storeTypeClaimsQuery,
} from '../../../services/react-query/optionMasterQuery';

export const ClaimCreate = () => {
  const [showStoreField, setShowStoreField] = useState('');
  const { control, setValue, getValues, handleSubmit, watch, reset } = useForm({
    defaultValues: { warranties: [] },
  });
  const [, setIsSubmitting] = useState(false);
  const { isPending: isPendingUpload, mutate: mutateUpload } = onUploadFile({
    onSuccessCallBack: () => {
      setIsSubmitting(false);
    },
  });

  const { mutate: onCallTele, isPending: isPendingTele } = useTeleQuery({ reset, getValues });
  const { mutate: onCallZipCode, isPending: isPendingZipCode } = useZipcode({ reset, getValues });
  const { data: channelClaim, isLoading: isLoadingChannelClaim } = channelClaimQuery();
  const { data: storeTypeChannelOptions, isLoading: isPendingStoreTypeChannel } =
    storeTypeClaimsQuery(showStoreField);

  const watchChannelClaim = watch('channelClaimId');

  useEffect(() => {
    if (watchChannelClaim && channelClaim) {
      const selectedChannel = channelClaim.find((option) => option.value === watchChannelClaim);
      const channelClaimType = selectedChannel?.channelClaimType;
      if (['FOCUS', 'STORE'].includes(channelClaimType)) {
        setShowStoreField(channelClaimType);
        setValue('isCreateNewAddress', 'false');
        setValue('customerAddressId', '');
        setValue('zipCode', '');
        setValue('provinceId', '');
        setValue('districtId', '');
        setValue('subDistrictId', '');
        setValue('other', '');
      } else {
        setShowStoreField('');
      }
    }
  }, [watchChannelClaim, channelClaim, setValue]);

  const {
    productOptions,
    addressOptions,
    breakdownOptions,
    provinceOptions,
    districtOptions,
    subDistrictOptions,
    addressDataOptions,
  } = useOptions({
    watch,
  });

  const onSubmit = (values) => {
    const valuesWithCustomerCode = {
      ...values,
      isCreateNewAddress:
        !['FOCUS', 'STORE'].includes(showStoreField) && !addressOptions?.length
          ? 'true'
          : values.isCreateNewAddress,
      provinceId:
        typeof values.provinceId === 'object' ? values.provinceId.value : values.provinceId,
      districtId:
        typeof values.districtId === 'object' ? values.districtId.value : values.districtId,
      subDistrictId:
        typeof values.subDistrictId === 'object'
          ? values.subDistrictId.value
          : values.subDistrictId,
    };
    handleSubmitForm(valuesWithCustomerCode, setIsSubmitting, mutateUpload);
  };

  const handleOnEnter = useCallback(
    async (value, item) => {
      switch (item?.name) {
        case 'telephone':
          reset({ telephone: getValues('telephone') });
          onCallTele(value);
          break;
        case 'zipCode':
          onCallZipCode(value);
          break;
        default:
          break;
      }
    },
    [getValues],
  );

  const onChangeInfoClaim = (_, item) => {
    switch (item?.name) {
      case 'channelClaimId':
        setValue('storeId', '');
        break;
      case 'warrantyId':
        setValue('breakdownId', '');
        break;
      default:
        break;
    }
  };

  const onChange = (value, item) => {
    const addressNew = addressDataOptions?.find((address) => address.customerAddressId === value);

    if (addressNew) {
      setValue('zipCode', addressNew.zipcode);
      setValue('other', addressNew.other || '', { shouldValidate: true });
      setValue('provinceId', { value: addressNew.provinceId, label: addressNew.provinceName });
      setValue('districtId', { value: addressNew.districtId, label: addressNew.districtName });
      setValue('subDistrictId', {
        value: addressNew.subDistrictId,
        label: addressNew.subDistrictName,
      });

      setValue('provinceLabel', addressNew.provinceName);
      setValue('districtLabel', addressNew.districtName);
      setValue('subDistrictLabel', addressNew.subDistrictName);
    }

    switch (item.name) {
      case 'isCreateNewAddress':
        if (value.target.value === 'true') {
          setValue('customerAddressId', '');
          setValue('other', '', { shouldValidate: true });
        }
        break;
      case 'zipCode':
        setValue('provinceId', '');
        setValue('districtId', '');
        setValue('subDistrictId', '');
        break;
      case 'provinceId':
        setValue('districtId', '');
        setValue('subDistrictId', '');
        break;
      case 'districtId':
        setValue('subDistrictId', '');
        break;
      case 'warrantyId':
        setValue('breakdownId', '');
        break;
      default:
        break;
    }
  };

  return (
    <Spin
      spinning={
        isPendingStoreTypeChannel || isPendingTele || isPendingZipCode || isLoadingChannelClaim
      }
    >
      <StyledContainer>
        <TextLarge text="ยื่นเรื่องเคลม" />
        <Row gutter={[24, 24]}>
          <Col span={14}>
            <CardTheme>
              <TextLarge text="ข้อมูลส่วนตัว" />
              <RenderForm
                control={control}
                setValue={setValue}
                getValues={getValues}
                onChange={onChange}
                forms={FORM_CLAIM_CREATE({
                  onEnter: handleOnEnter,
                  provinceOptions,
                  districtOptions,
                  subDistrictOptions,
                  addressOptions,
                  watch,
                  showStoreField,
                })}
              />
              <ContainerButton center>
                {isPendingUpload ? (
                  <Spin spinning={isPendingUpload}></Spin>
                ) : (
                  <ButtonTheme useFor="SUBMIT" title="บันทึก" onClick={handleSubmit(onSubmit)} />
                )}
              </ContainerButton>
            </CardTheme>
          </Col>
          <Col span={10}>
            <CardTheme>
              <TextLarge text="ข้อมูลการแจ้งเคลม" />
              <RenderForm
                control={control}
                setValue={setValue}
                onChange={onChangeInfoClaim}
                getValues={getValues}
                forms={FORM_CLAIM_DATA({
                  productOptions,
                  breakdownOptions,
                  channelClaim,
                  storeTypeChannelOptions,
                  showStoreField,
                })}
              />
            </CardTheme>
          </Col>
        </Row>
      </StyledContainer>
    </Spin>
  );
};
